/**
 * CalibrationForm — 6 level dropdowns + 6 score inputs + notes (AC-10.2, UI-06).
 */

import React, { useState } from 'react';
import type { CalibrationRecord } from '@sdd-maturity/shared-types';
import { Button } from './Button';

interface CalibrationFormProps {
  featureFormattedId: string;
  bedrockScores: [number, number, number, number, number, number];
  onSubmit: (record: Partial<CalibrationRecord>) => Promise<void>;
}

const DIMENSION_NAMES = [
  'Discovery Planning Maturity',
  'Problem Framing Quality',
  'Hypothesis Validation Rigor',
  'Solution Option Exploration',
  'User Validation Depth',
  'Discovery-to-Delivery Handoff',
] as const;

export function CalibrationForm({
  featureFormattedId,
  bedrockScores,
  onSubmit,
}: CalibrationFormProps): React.JSX.Element {
  const [humanScores, setHumanScores] = useState<number[]>(Array(6).fill(50) as number[]);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  function setScore(idx: number, value: number) {
    setHumanScores((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  }

  function validate(): string[] {
    const errs: string[] = [];
    humanScores.forEach((score, i) => {
      if (score < 0 || score > 100) {
        errs.push(`Dimension ${i + 1} score must be 0–100`);
      }
    });
    return errs;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);
    setIsSubmitting(true);
    try {
      await onSubmit({
        featureFormattedId,
        bedrockScores: bedrockScores,
        humanScores: humanScores as [number, number, number, number, number, number],
        notes,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} data-testid="calibration-form" aria-label="Human scoring form">
      {errors.length > 0 && (
        <div role="alert" style={{ color: 'var(--genai-sem-error)', marginBottom: '12px' }}>
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      {DIMENSION_NAMES.map((name, idx) => (
        <div key={idx} style={{ marginBottom: '12px' }}>
          <label
            htmlFor={`score-${idx}`}
            style={{ display: 'block', fontSize: 'var(--genai-text-base)', color: 'var(--genai-sem-text-secondary)', marginBottom: '4px' }}
          >
            {name} (Bedrock: {bedrockScores[idx]})
          </label>
          <input
            id={`score-${idx}`}
            type="number"
            min={0}
            max={100}
            value={humanScores[idx] ?? 50}
            onChange={(e) => setScore(idx, Number(e.target.value))}
            aria-invalid={((humanScores[idx] ?? 50) < 0 || (humanScores[idx] ?? 50) > 100) ? true : undefined}
            data-testid={`score-input-${idx}`}
            style={{
              width: '80px',
              padding: '4px 8px',
              border: '1px solid var(--genai-sem-border)',
              borderRadius: '4px',
              background: 'var(--genai-sem-input-bg, var(--genai-sem-surface-bg))',
              color: 'var(--genai-sem-text-primary)',
            }}
            required
          />
        </div>
      ))}

      <div style={{ marginBottom: '12px' }}>
        <label htmlFor="notes" style={{ display: 'block', fontSize: 'var(--genai-text-base)', color: 'var(--genai-sem-text-secondary)', marginBottom: '4px' }}>
          Notes
        </label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid="calibration-notes"
          rows={3}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid var(--genai-sem-border)',
            borderRadius: '4px',
            background: 'var(--genai-sem-input-bg, var(--genai-sem-surface-bg))',
            color: 'var(--genai-sem-text-primary)',
            resize: 'vertical',
          }}
        />
      </div>

      <Button
        type="submit"
        variant="primary"
        disabled={isSubmitting}
        data-testid="submit-calibration"
      >
        {isSubmitting ? 'Submitting...' : 'Submit Calibration'}
      </Button>
    </form>
  );
}
